import { Component, ViewEncapsulation, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { of, Subscription, forkJoin, catchError, switchMap } from 'rxjs';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatRippleModule } from '@angular/material/core';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf, NgClass, NgFor } from '@angular/common';

import { pageTransitions } from '../../../shared/animations/page-transitions';
import { DocumentService } from '../../services/documents.service';
import { UNDERSCORE, PERCENTAGE, EMPTYSPACE, OrderBy, OrderByDirection } from '../../../shared/utils/constant';
import { Document, DocumentLine, FieldOfDocument, HeatNumbers } from '../../models/document';
import { UserService } from '../../../shared/services/user.service';
import { Batch } from '../../../order/models/batch';
import { BatchesService } from '../../../order/services/batches.service';
import { PaginatedResult } from '../../../shared/models/paginated-result';
import { batchesFilter } from '../../utils/batches.utils';
import { SafeUrlPipe } from '../../../shared/pipes/safeurl.pipe';
import { DatePipe } from '../../../shared/pipes/date.pipe';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { OfflineComponent } from '../../../shared/components/offline/offline.component';
import { AfterViewInitDirective } from '../../../shared/directives/after-view-init.directive';
import { BackButtonComponent } from '../../../shared/components/back-button/back-button.component';
import { ScrollerDirective } from '../../../shared/directives/scroller.directive';
import { DbAnalyticsService } from '../../../shared/services/db-analytics.service';

@Component({
	selector: 'copo-certificate-card',
	templateUrl: './certificate-card.component.html',
	styleUrl: './certificate-card.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		ScrollerDirective,
		NgIf,
		BackButtonComponent,
		TranslateModule,
		MatRippleModule,

		MatButtonModule,
		MatIconModule,
		AfterViewInitDirective,
		MatExpansionModule,
		MatDividerModule,
		NgClass,
		NgFor,
		OfflineComponent,
		LoadingSpinnerComponent,
		DatePipe,
		SafeUrlPipe
	]
})
export class CertificateCardComponent implements OnDestroy {
	certificateFields: readonly FieldOfDocument[] = [
		'id',
		'type',
		'subtype',
		'date',
		'name',
		'paymentDueDate',
		'supplierVatNumber',
		'payerName',
		'payerVatNumber',
		'documentLines',
		'millCode',
		'soldtoName',
		'shiptoName',
		'customerOrderReference',
		'supplierOrderReference'
	];

	certificateData?: Document;
	batchData: PaginatedResult<Batch> | undefined;
	value?: DocumentLine[];
	id: number;
	certificateDisplayed = false;
	allBatchesDisplayed = true;
	certificateSub: Subscription;
	isLoading = false;
	isOffline = false;
	isDocumentNotFound = false;
	heatNumbers: HeatNumbers[] = [];
	readonly UNDERSCORE = UNDERSCORE;
	readonly PERCENTAGE = PERCENTAGE;
	readonly EMPTYSPACE = EMPTYSPACE;
	constructor(
		private readonly documentService: DocumentService,
		private readonly route: ActivatedRoute,
		private readonly userService: UserService,
		private readonly batchService: BatchesService,
		private readonly dbAnalyticsService: DbAnalyticsService
	) {
		this.id = <number> this.route.snapshot.params['id'];
		this.isOffline = !navigator.onLine;
		const { user } = this.userService;
		this.certificateDisplayed = user.hasServiceParameterEqualsTo('PDOCS', 'Certificate', 'True');
		this.isLoading = true;
		this.certificateDisplayed = user.hasServiceParameterEqualsTo('PDOCS', 'Certificate', 'True');
		this.certificateSub = this.documentService.getDocumentDetails(this.id, this.certificateFields)
			.pipe(
				switchMap(
					certificateData => forkJoin([
						of(certificateData),
						this.getBatchData(certificateData.documentLines),
					])
				)
			)
			.subscribe({
				next: ([certificateData, batchData]) => {
					if (Object.keys(certificateData).length === 0) {
						this.isDocumentNotFound = true;
						this.isLoading = false;
					}
					this.certificateData = certificateData;
					this.batchData = batchData;
					for (const certificate of this.certificateData.documentLines) {
						certificate.isSelected = true;
					}
					this.isLoading = false;
					this.getUniqueHeatNumber();
				},
				error: (err: unknown) => {
					console.error('error loading', err);
					this.isLoading = false;
				}
			});
	}

	getDownloadDocumentUrl(id: number, filename: string) {
		return this.documentService.getDownloadDocumentUrl(id, filename);
	}

	trackDownload(id: number) {
		this.dbAnalyticsService.trackAction('DOWNLOAD_PDF', { documentType: 'CERTIFICATE', documentId: String(id) });
	}

	getDocumentURL(id: number, filename: string) {
		return this.documentService.getViewDocumentUrl(id, filename);
	}

	getUniqueHeatNumber() {
		const heatReference = new Set<string>();
		if (this.certificateData?.documentLines === undefined) {
			return;
		}
		for (const docLine of this.certificateData.documentLines) {
			heatReference.add(docLine.heatNumber);
		}
		for (const heatRef of heatReference) {
			const heatNumber = {
				key: heatRef,
				isSelected: false
			};
			this.heatNumbers.push(heatNumber);
		}
	}

	displayAllBatches() {
		this.allBatchesDisplayed = true;
		if (this.certificateData?.documentLines === undefined) {
			return;
		}
		for (const docLine of this.certificateData.documentLines) {
			docLine.isSelected = true;
		}
	}

	filterBatch(heatNum: HeatNumbers) {
		this.allBatchesDisplayed = false;
		if (this.certificateData?.documentLines === undefined) {
			return;
		}
		for (const heatNumber of this.heatNumbers) {
			heatNumber.isSelected = heatNum.key === heatNumber.key;
		}
		for (const documentLine of this.certificateData.documentLines) {
			documentLine.isSelected = heatNum.key === documentLine.heatNumber;
		}
	}

	getBatchData(documentLine: DocumentLine[] | undefined) {
		if (documentLine === undefined) {
			return of(undefined);
		}
		return this.batchService.getBatches(
			[
				'qtyBatchNet',
				'qtyBatchGross',
				'batchReference'
			],
			batchesFilter(documentLine.map(certificateBatch => certificateBatch.batchNumber)),
			0,
			50,
			new OrderBy('batchReference', OrderByDirection.DESC),
			false
		)
			.pipe(catchError((err: unknown) => {
				console.error('Batch not found :', err);
				return of(undefined);
			}));
	}

	getQtyBatch(batchReference: string, type: string) {
		if (this.batchData && type === 'WEIGHT') {
			for (const batch of this.batchData.results) {
				if (batch.batchReference === batchReference) {
					return batch.qtyBatchGross;
				}
			}
		}
		return UNDERSCORE;
	}

	getMillOrderNumber(documentLine: DocumentLine) {
		if (documentLine.millOrderReference
			&& documentLine.millOrderReferenceItem) {
			return `${ documentLine.millOrderReference }/${ documentLine.millOrderReferenceItem }`;
		} else if (documentLine.millOrderReference
			&& !documentLine.millOrderReferenceItem) {
			return documentLine.millOrderReference;
		}
		return UNDERSCORE;
	}

	getOrderNumber(documentLine: DocumentLine) {
		if (this.certificateData?.customerOrderReference && documentLine.customerOrderItemReference) {
			return `${ this.certificateData.customerOrderReference }/${ documentLine.customerOrderItemReference }`;
		} else if (this.certificateData?.customerOrderReference && !documentLine.customerOrderItemReference) {
			return this.certificateData.customerOrderReference;
		}
		return UNDERSCORE;
	}

	ngOnDestroy() {
		this.certificateSub.unsubscribe();
	}
}
